import InscreenObserver from '@finn-no/inscreen-observer';
import Spinner from '@finn-no/troika-react-spinner';
import { DeviceType } from '@finn-no/displayads-client';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Except } from 'type-fest';
import Ads from '../Ads';
import TrackEmpty from '../TrackEmpty';
import Tracking from '../tracking';
import Fetcher from './fetcher';
import VerticalSelector from '../VerticalSelector';
import { olderThan24hrAgo } from '../helpers';
import { MessageBus } from '@podium/browser';
import Tags from '../Tags';

type RecommendationsResponse = import('../types').RecommendationsResponse;

type RecommendationsStreamProps = {
    baseUrl: string;
    url?: string;
    product: string;
    rows: string;
    pages: string;
    type: string;
    adId: string;
    fetch?: typeof window.fetch;
    position: string;
    title: string;
    debug?: boolean;
    headingLevel: 1 | 2 | 3 | 4 | 5;
    deviceType: DeviceType;
    showVerticalFilter: boolean;
    useFallBackProduct?: boolean;
    showKeywords: boolean;
    suppressTitle: boolean;
    previewId?: string;
    previewType?: string;
    onEmpty?: () => void;
} & Except<
    React.ComponentProps<typeof Ads>,
    'items' | 'trackAdClick' | 'tracking' | 'rows'
>;

const RecommendationsStream: React.FC<RecommendationsStreamProps> = (props) => {
    const {
        fetch,
        baseUrl,
        rows,
        pages,
        type,
        adId,
        title,
        onEmpty,
        url,
        debug,
        headingLevel,
        showVerticalFilter,
        useFallBackProduct,
        enableLargePosition,
        showKeywords,
        previewId,
        previewType,
        suppressTitle,
        ...passThroughProps
    } = props;

    const { product, position, deviceType } = props;
    const isFrontpage = position.split('_')[0] === 'm-frontpage';
    const isBapMarkedFrontpage = position.split('_')[0] === 'bap-browse';
    const LOCALSTORAGE_VERTICALPICKER = 'recommendation-vertical-picker';
    const LOCALSTORAGE_VERTICALPICKER_TIMEOUT =
        'recommendation-vertical-picker-last-set';

    const verticalSelectorHasExpired = (): boolean => {
        const oldDate = JSON.parse(
            localStorage.getItem(LOCALSTORAGE_VERTICALPICKER_TIMEOUT)
        );
        return olderThan24hrAgo(oldDate);
    };

    const getVerticalPicker = () => {
        if (verticalSelectorHasExpired()) {
            localStorage.removeItem(LOCALSTORAGE_VERTICALPICKER);
            localStorage.removeItem(LOCALSTORAGE_VERTICALPICKER_TIMEOUT);
            return undefined;
        }
        return JSON.parse(localStorage.getItem(LOCALSTORAGE_VERTICALPICKER));
    };
    const hasSelectedItem = getVerticalPicker();

    const [state, setState] = useState({
        items: [],
        isEmpty: false,
        hasContent: false,
        isLoading: true,
        uuid: '',
        version: '',
        trackingContext: null,
        shouldFetchMore: false,
        fetchRestOnClick: false, // Behind unleash
        bottomReached: false,
        clickIsActive: false,
        currentVerticalSelected:
            hasSelectedItem && isFrontpage ? hasSelectedItem[0] : product,
        currentVerticalSelectedStr:
            hasSelectedItem && isFrontpage ? hasSelectedItem[1] : 'Alle',
        reRender: false,
    });
    const [keyWord, setKeyWord] = useState<string>('');
    const { uuid, version, trackingContext } = state;
    const messageBus = new MessageBus();

    const tracking = useMemo(
        () =>
            new Tracking({
                position,
                uuid,
                version,
                deviceType,
                trackingContext,
            }),
        [position, uuid, version, deviceType, trackingContext]
    );

    const fetcher = useMemo(() => new Fetcher(props.fetch), [props.fetch]);

    const fetchMoreItems = useCallback(() => {
        setState((state) => ({
            ...state,
            isLoading: true,
        }));

        return fetcher
            .getFetchMore(baseUrl)
            .then((data) => {
                let nextState = {
                    isLoading: false,
                };

                setState((state) => ({
                    ...state,
                    ...nextState,
                    hasContent: true,
                    isEmpty: false,
                    shouldFetchMore: data.items.length > 0,
                    items: [...state.items, ...data.items],
                    clickIsActive: false,
                }));
            })
            .catch((error) => {
                setState((state) => ({ ...state, isLoading: false }));
                console.error(error);
            });
    }, [baseUrl, fetcher]);

    const scrollFooterEle = useRef<HTMLDivElement>(null);
    const scrollObserver = useRef(null);
    const loadMoreBtn = useRef(null);
    const rowsToRender = parseInt(pages) * parseInt(rows);
    const bottomOut = 'bottom_out';
    const clickCounter = 'load_more_clicked';

    const incrementBottomOut = useCallback(
        (isAbsoluteBottom) => {
            sendMetrics(
                `${url}/tracking/${bottomOut}/${isAbsoluteBottom}/${position}?ts=${new Date().getTime()}`
            );
        },
        [position, url]
    );

    const incrementCounter = useCallback(
        (label = 'unknown') => {
            sendMetrics(
                `${url}/tracking/${label}/${position}?ts=${new Date().getTime()}`
            );
        },
        [position, url]
    );
    useEffect(() => {
        if (
            !state.fetchRestOnClick &&
            state.items.length >= rowsToRender &&
            type === 'recommend'
        ) {
            if (loadMoreBtn.current)
                loadMoreBtn.current.parentNode.classList.add('u-display-block');
            return;
        }

        if (
            state.fetchRestOnClick &&
            state.items.length === rowsToRender &&
            !state.bottomReached
        ) {
            setState((state) => ({
                ...state,
                bottomReached: true,
            }));
            incrementBottomOut(true);
        }
        if (
            !scrollObserver.current &&
            scrollFooterEle.current &&
            state.shouldFetchMore &&
            state.items.length > 0 &&
            state.clickIsActive === false
        ) {
            scrollObserver.current = new InscreenObserver(
                () => fetchMoreItems(),
                { proximity: 200, once: true }
            );
            scrollObserver.current.observe(scrollFooterEle.current);
            return () => {
                scrollObserver.current.disconnect();
                scrollObserver.current = null;
            };
        }
    }, [
        fetchMoreItems,
        state.items.length,
        state.shouldFetchMore,
        rowsToRender,
        state.fetchRestOnClick,
        state.bottomReached,
        incrementBottomOut,
        state.clickIsActive,
        type,
    ]);

    useEffect(() => {
        if (loadMoreBtn.current) {
            const fetchMoreBtnInscreen = new InscreenObserver(
                () => incrementBottomOut(false),
                { proximity: 0, once: true }
            );
            fetchMoreBtnInscreen.observe(loadMoreBtn.current);
        }
    }, [incrementBottomOut]);

    useEffect(() => {
        let mounted = true;

        setState((state) => ({
            ...state,
            items: [],
            isLoading: true,
            shouldFetchMore: false,
        }));

        fetcher
            .getRecommendations(baseUrl, {
                product: useFallBackProduct
                    ? product
                    : state.currentVerticalSelected,
                rows,
                pages,
                type,
                adId,
                position,
                debug,
                previewId,
                previewType,
            })
            .then((data) => {
                if (previewId.length > 0) {
                    return fetcher
                        .getPreview(baseUrl, previewId, previewType)
                        .then((preview): Promise<RecommendationsResponse> => {
                            return new Promise((res) => {
                                if (preview.items) {
                                    data.items.splice(
                                        0,
                                        preview.items.length,
                                        ...preview.items
                                    );
                                }
                                return res(data);
                            });
                        });
                } else {
                    return Promise.resolve(data);
                }
            })
            .then((data) => {
                if (!mounted) {
                    // Fix "React can't run state updates on an unmounted component" warnings
                    return;
                }
                let nextState = {
                    isLoading: false,
                };

                if (
                    data.items.length === 0 &&
                    state.currentVerticalSelectedStr === 'Alle'
                ) {
                    setState((state) => ({
                        ...state,
                        ...nextState,
                        isEmpty: state.items.length === 0,
                        reRender: false,
                    }));

                    return data;
                }
                setState((state) => ({
                    ...state,
                    ...nextState,
                    hasContent: true,
                    isEmpty: false,
                    uuid: data.uuid,
                    trackingContext: data['tracking-context'],
                    version: data.version,
                    items: data.items,
                    shouldFetchMore: true,
                    reRender: false,
                }));
            })
            .catch((error) => {
                if (!mounted) {
                    // Fix "React can't run state updates on an unmounted component" warnings
                    return;
                }

                setState((state) => ({
                    ...state,
                    isLoading: false,
                    reRender: false,
                }));
                console.error(error);
            });

        return () => {
            mounted = false;
        };
    }, [
        adId,
        baseUrl,
        fetcher,
        pages,
        product,
        rows,
        type,
        position,
        debug,
        state.currentVerticalSelected,
        useFallBackProduct,
        state.currentVerticalSelectedStr,
        showVerticalFilter,
        isFrontpage,
        state.reRender,
        previewType,
        previewId,
        suppressTitle,
        isBapMarkedFrontpage,
    ]);

    // HANDLING KEYWORDS
    useEffect(() => {
        if (keyWord.length === 0) return;
        setState((state) => ({
            ...state,
            items: [],
            isLoading: true,
            shouldFetchMore: false,
        }));

        fetcher
            .getRecommendationsFromQuery(baseUrl, keyWord, position)
            .then((data) => {
                setState((state) => ({
                    ...state,
                    hasContent: true,
                    isEmpty: false,
                    items: data.items,
                    shouldFetchMore: false,
                    reRender: false,
                    isLoading: false,
                }));
            })
            .catch((err) => {
                setState((state) => ({
                    ...state,
                    isLoading: false,
                    reRender: false,
                }));
            });
    }, [keyWord, baseUrl, url, fetcher, position]);

    const trackAdClick = useCallback(
        (adId: string, trackingUrlClick: string[]) => {
            try {
                window.localStorage.setItem(
                    'ec-ref-id',
                    JSON.stringify({
                        referrer: window.location.href,
                        append: `type:${
                            type || 'recommend'
                        },product:${product},position:${position}`,
                    })
                );
            } catch (e) {
                // ignore error
            }

            tracking.trackAdClick(adId, trackingUrlClick, position, type);
        },
        [position, product, tracking, type]
    );

    const { isLoading, isEmpty, hasContent } = state;
    const nothingToRender = !isLoading && isEmpty;
    if (nothingToRender) {
        return <TrackEmpty tracking={tracking} onEmpty={onEmpty} />;
    }

    function getMoreOnClick() {
        setState((state) => ({
            ...state,
            shouldFetchMore: true,
            fetchRestOnClick: true,
            clickIsActive: true,
        }));
        fetchMoreItems();
        if (loadMoreBtn.current)
            loadMoreBtn.current.parentNode.classList.remove('u-display-block');
        incrementCounter(clickCounter);
    }

    const validHeadingLevel = Math.max(1, Math.min(5, headingLevel));
    const Heading = `h${validHeadingLevel}` as 'h2';

    const handleVerticalClick = (e) => {
        e.preventDefault();
        const vertical = e.target.value;
        const verticalName = e.target[e.target.selectedIndex].text;
        setState((state) => ({
            ...state,
            shouldFetchMore: true,
            fetchRestOnClick: true,
            items: [],
            currentVerticalSelected: vertical,
            currentVerticalSelectedStr: verticalName,
        }));
        const verticalObject = [vertical, verticalName];
        localStorage.setItem(
            LOCALSTORAGE_VERTICALPICKER,
            JSON.stringify(verticalObject)
        );
        localStorage.setItem(
            LOCALSTORAGE_VERTICALPICKER_TIMEOUT,
            Date.now().toString()
        );
    };

    messageBus.subscribe(
        'recommendations-podlet',
        'reload-recommendations',
        () => {
            setState((state) => ({
                ...state,
                shouldFetchMore: false,
                fetchRestOnClick: false,
                items: [],
                reRender: true,
                // hasContent: false
            }));
        }
    );

    const tagHasBeenSelected = (e: any) => {
        setKeyWord(e.target.innerHTML);
    };

    const resetTags = () => {
        setKeyWord('');
        setState((state) => ({
            ...state,
            reRender: true,
        }));
    };

    return (
        <div aria-label={hasContent ? undefined : title}>
            {hasContent && (
                <div
                    style={{ alignItems: 'center' }}
                    className="grid grid--auto-height grid--vertical-center u-ma16"
                >
                    {!suppressTitle && (
                        <Heading className="grid__unit u-t3 u-display-inline u-pr8">
                            {title}
                        </Heading>
                    )}
                    {showVerticalFilter && isFrontpage && (
                        <VerticalSelector
                            product={product}
                            verticalStr={state.currentVerticalSelectedStr}
                            verticalVal={state.currentVerticalSelected}
                            handleVerticalClick={handleVerticalClick}
                            url={url}
                        />
                    )}
                    {/*isFrontpage && ( OBS! display when FD-4120 migrate to eik is finished!!
                        <a
                            href="/anbefalinger/historikk"
                            className="link link--dark u-caption"
                        >
                            (Hvorfor anbefaler vi disse annonsene?)
                        </a>
                    )*/}
                    {isBapMarkedFrontpage && showKeywords && (
                        <div
                            className="u-display-block"
                            style={{ width: '100%' }}
                        >
                            <Tags
                                baseUrl={baseUrl}
                                position={position}
                                updateRecommendations={tagHasBeenSelected}
                                resetRecommendations={resetTags}
                                trackClick={trackAdClick}
                            />
                        </div>
                    )}
                </div>
            )}
            <Ads
                items={state.items}
                enableLargePosition={enableLargePosition}
                trackAdClick={trackAdClick}
                tracking={tracking}
                headingLevel={(validHeadingLevel + 1) as 2}
                url={url}
                feedbackKeyword={keyWord}
                {...passThroughProps}
            />
            <div ref={scrollFooterEle} />

            {state.isLoading && <Spinner />}

            {type === 'recommend' &&
            keyWord.length === 0 &&
            product !== 'blink-preview' ? (
                <div className="u-display-none u-text-center">
                    <button
                        className="button button--cta u-mb16"
                        onClick={getMoreOnClick}
                        ref={loadMoreBtn}
                    >
                        Se flere anbefalinger
                    </button>
                </div>
            ) : null}
        </div>
    );
};

function sendMetrics(url) {
    try {
        fetch(url);
    } catch (err) {
        console.log(err);
    }
}

export default RecommendationsStream;
